import { ReactNode } from 'react';
import { featureFlagClient } from '@trello/feature-flag-client';

export type JSONPrimitive = string | number | boolean;

export class StringNotFoundAtPathError extends Error {}

export interface NestedStrings {
  [key: string]: JSONPrimitive | NestedStrings;
}

export interface StringSubstitutions {
  [key: string]: string;
}

export interface ReactSubstitutions {
  [key: string]: StringSubstitutions[keyof StringSubstitutions] | ReactNode;
}

export type AllSubstitutions = StringSubstitutions | ReactSubstitutions;
export type AllSubstitutionValues = AllSubstitutions[keyof AllSubstitutions];

class Hole {
  key: string;
  constructor(key: string) {
    this.key = key;
  }
}
class Literal {
  value: string;
  constructor(value: string) {
    this.value = value;
  }
}

const lookupPath = (
  obj: NestedStrings,
  path: string[],
): NestedStrings | JSONPrimitive => {
  let cursor: NestedStrings | JSONPrimitive = obj;
  path.forEach((key: string) => {
    if (
      typeof cursor === 'object' &&
      Object.prototype.hasOwnProperty.call(cursor, key)
    ) {
      cursor = cursor[key];
    }
  });

  return cursor;
};

export const lookupRebrandedString = (
  strings: NestedStrings,
  keyPath: string[],
) => {
  const isTeamsRebrandActive = featureFlagClient.get(
    'btg.teams-rebrand-web',
    false,
  );

  const isBCRebrandActive = featureFlagClient.get(
    'nusku.rename-bc-premium',
    false,
  );

  if (isTeamsRebrandActive || isBCRebrandActive) {
    const tryKeys = [];
    const prevKeyPath = keyPath.slice(0, -1);
    const lastKeyPath = keyPath[keyPath.length - 1];

    if (isTeamsRebrandActive && isBCRebrandActive) {
      // Given ['team onboarding', 'team members'], look up:
      // ['team onboarding', 'team members teams-rebrand bc-rebrand']
      // ['team onboarding', 'team members bc-rebrand teams-rebrand']
      tryKeys.push(
        prevKeyPath.concat(`${lastKeyPath} teams-rebrand bc-rebrand`),
      );
      tryKeys.push(
        prevKeyPath.concat(`${lastKeyPath} bc-rebrand teams-rebrand`),
      );
    }

    if (isTeamsRebrandActive) {
      // Given ['team onboarding', 'team members'], look up:
      // ['team onboarding', 'team members teams-rebrand']
      tryKeys.push(prevKeyPath.concat(`${lastKeyPath} teams-rebrand`));
    }

    if (isBCRebrandActive) {
      // Given ['team onboarding', 'team members'], look up:
      // ['team onboarding', 'team members bc-rebrand']
      tryKeys.push(prevKeyPath.concat(`${lastKeyPath} bc-rebrand`));
    }

    for (const path of tryKeys) {
      const rebrandedValue = lookupPath(strings, path);

      if (rebrandedValue && typeof rebrandedValue === 'string') {
        return rebrandedValue;
      }
    }
  }
};

const lookupString = (strings: NestedStrings, keyPath: string[]) => {
  const rebrandedValue = lookupRebrandedString(strings, keyPath);

  if (rebrandedValue) {
    return rebrandedValue;
  }

  const val = lookupPath(strings, keyPath);
  if (val) {
    if (typeof val !== 'string') {
      throw new StringNotFoundAtPathError(
        `String not found at path ${keyPath}`,
      );
    }

    return val;
  }
};

const parseFormatString = (formatString: string) => {
  return formatString
    .split(/(\{[^{}]+\})/)
    .map(function (group) {
      if (group.length === 0) {
        return null;
      }
      if (group[0] === '{') {
        return new Hole(group.substr(1, group.length - 2));
      } else {
        return new Literal(group);
      }
    })
    .filter((a) => a);
};

export function normalizeKeyPath(path: string | string[]) {
  if (Array.isArray(path)) {
    return path.filter((a) => a);
  }

  return path.split('.');
}

export function lookup(strings: NestedStrings, path: string | string[]) {
  const keyPath = normalizeKeyPath(path);
  const formatString = lookupString(strings, keyPath);

  if (formatString) {
    return parseFormatString(formatString);
  }
}

export function makeVisitor(
  onLiteral: (literal: Literal, substitutions?: AllSubstitutions) => string,
  onHole: (
    hole: Hole,
    substitutions?: AllSubstitutions,
    shouldEscapeStrings?: boolean,
    canOmitSubstitutions?: boolean,
  ) => AllSubstitutionValues,
) {
  return (
    holesAndLiterals: (Hole | Literal)[],
    substitutions?: AllSubstitutions,
    shouldEscapeStrings: boolean = true,
    canOmitSubstitutions: boolean = false,
  ) => {
    return holesAndLiterals.map((holeOrLiteral) => {
      if (holeOrLiteral instanceof Hole) {
        return onHole(
          holeOrLiteral,
          substitutions,
          shouldEscapeStrings,
          canOmitSubstitutions,
        );
      } else {
        return onLiteral(holeOrLiteral, substitutions);
      }
    });
  };
}

export function visit(
  strings: NestedStrings,
  keyPath: string[],
  visitor: ReturnType<typeof makeVisitor>,
  substitutions?: AllSubstitutions,
  shouldEscapeStrings: boolean = true,
  canOmitSubstitutions: boolean = false,
) {
  const holesAndLiterals = lookup(strings, keyPath) as (Hole | Literal)[];
  if (holesAndLiterals) {
    return visitor(
      holesAndLiterals,
      substitutions,
      shouldEscapeStrings,
      canOmitSubstitutions,
    );
  }
}
